import React, { useState, useEffect } from 'react';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { GripVertical, X, Check } from 'lucide-react';
import { FIXED_GROUP_COLUMNS, MAX_GROUP_COLUMNS, OPTIONAL_STANDARD_GROUP_COLUMNS } from 'features/activity/config/groupColumns';
import { usePreferences } from 'shared/context/preferencesContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import { primary } from 'shared/theme/trodoColors';

const GroupColumnConfigModal = ({ isOpen, onClose, teamId, groupKey, availableCustomProps = [] }) => {
  const { alert: appAlert } = useAppDialog();
  const { updateGroupsTableColumns, resetGroupsTableColumns, getGroupsTableColumns, loading } = usePreferences();
  const [enabledColumns, setEnabledColumns] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const current = getGroupsTableColumns(groupKey);
      const fixedKeys = Object.keys(FIXED_GROUP_COLUMNS);
      const enabledSet = new Set([...fixedKeys, ...(current.enabled || [])]);
      const enabled = [...enabledSet];
      let order = [...(current.order || [])].filter(k => enabledSet.has(k));
      fixedKeys.forEach(f => { if (!order.includes(f)) order.unshift(f); });
      enabled.forEach(k => { if (!order.includes(k)) order.push(k); });
      setEnabledColumns(enabled);
      setColumnOrder(order);
      setHasChanges(false);
    }
  }, [isOpen, groupKey, getGroupsTableColumns]);

  const fixedKeys = Object.keys(FIXED_GROUP_COLUMNS);
  const optionalStandardKeys = Object.keys(OPTIONAL_STANDARD_GROUP_COLUMNS);
  const customPropKeys = availableCustomProps.filter(k => !FIXED_GROUP_COLUMNS[k] && !OPTIONAL_STANDARD_GROUP_COLUMNS[k]);
  const maxNonFixed = MAX_GROUP_COLUMNS - fixedKeys.length;
  const isFixed = (key) => !!FIXED_GROUP_COLUMNS[key];

  const getColumnLabel = (key) => {
    if (FIXED_GROUP_COLUMNS[key]) return FIXED_GROUP_COLUMNS[key].label;
    if (OPTIONAL_STANDARD_GROUP_COLUMNS[key]) return OPTIONAL_STANDARD_GROUP_COLUMNS[key].label;
    return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const handleToggleColumn = (columnKey) => {
    if (isFixed(columnKey)) return;
    const newEnabled = enabledColumns.includes(columnKey)
      ? enabledColumns.filter(k => k !== columnKey)
      : [...enabledColumns, columnKey];
    const nonFixedEnabled = newEnabled.filter(k => !isFixed(k));
    if (nonFixedEnabled.length > maxNonFixed && !enabledColumns.includes(columnKey)) return;
    setEnabledColumns(newEnabled);
    const newOrder = columnOrder.filter(k => newEnabled.includes(k));
    if (!enabledColumns.includes(columnKey)) newOrder.push(columnKey);
    setColumnOrder(newOrder);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!teamId) return;
    setSaving(true);
    try {
      await updateGroupsTableColumns(groupKey, enabledColumns, columnOrder);
      setHasChanges(false);
      onClose();
    } catch {
      await appAlert('Failed to save column preferences. Please try again.', { title: 'Save failed', variant: 'error' });
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!teamId) return;
    setSaving(true);
    try {
      await resetGroupsTableColumns(groupKey);
      const current = getGroupsTableColumns(groupKey);
      setEnabledColumns([...(current.enabled || [])]);
      setColumnOrder([...(current.order || [])]);
      setHasChanges(false);
    } catch {
      await appAlert('Failed to reset column preferences. Please try again.', { title: 'Reset failed', variant: 'error' });
    } finally { setSaving(false); }
  };

  const handleCancel = () => {
    const current = getGroupsTableColumns(groupKey);
    setEnabledColumns([...(current.enabled || [])]);
    setColumnOrder([...(current.order || [])]);
    setHasChanges(false);
    onClose();
  };

  if (!isOpen) return null;

  const orderedEnabled = columnOrder.filter(k => enabledColumns.includes(k));
  const nonFixedEnabled = enabledColumns.filter(k => !isFixed(k));

  const allToggleable = [
    ...optionalStandardKeys.map(k => ({ key: k, label: getColumnLabel(k), group: 'Standard' })),
    ...customPropKeys.map(k => ({ key: k, label: getColumnLabel(k), group: 'Custom Properties' })),
  ];

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-[color:var(--surface-overlay)]" onClick={handleCancel} />
        <div className="relative bg-surface w-full max-w-[380px] rounded-xl border border-[color:var(--border-default)] shadow-xl overflow-hidden flex flex-col" style={{ maxHeight: 'min(90vh, 560px)' }}>
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[color:var(--border-default)] shrink-0">
            <span className="text-[13px] font-semibold text-[color:var(--text-primary)]">Edit Columns</span>
            <div className="flex items-center gap-3">
              <button onClick={handleReset} disabled={saving || loading} className="text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors disabled:opacity-40">Reset</button>
              <button onClick={handleCancel} className="p-1 rounded hover:bg-[color:var(--surface-sunken)] text-[color:var(--text-muted)] transition-colors"><X size={14} /></button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-subtle">
            {orderedEnabled.length > 0 && (
              <div className="px-3 pt-3 pb-1">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)] px-1 mb-1">Active · drag to reorder</p>
                {orderedEnabled.map((key, index) => (
                  <DraggableItem
                    key={key} columnKey={key} index={index}
                    label={getColumnLabel(key)} isRequired={isFixed(key)}
                    onMove={(from, to) => {
                      const newOrder = [...columnOrder];
                      const [removed] = newOrder.splice(from, 1);
                      newOrder.splice(to, 0, removed);
                      setColumnOrder(newOrder);
                      setHasChanges(true);
                    }}
                  />
                ))}
              </div>
            )}
            {orderedEnabled.length > 0 && <div className="border-t border-[color:var(--border-default)] mx-3 mt-2" />}
            <div className="px-3 pt-2.5 pb-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)] px-1 mb-1">
                Available (max {maxNonFixed} additional)
              </p>
              {allToggleable.length === 0 && (
                <p className="text-[12px] text-[color:var(--text-muted)] px-2 py-2">No additional columns available.</p>
              )}
              {['Standard', 'Custom Properties'].map(group => {
                const cols = allToggleable.filter(c => c.group === group);
                if (!cols.length) return null;
                return (
                  <div key={group} className="mb-2">
                    <p className="text-[10px] text-[color:var(--text-muted)] px-1 mb-0.5 italic">{group}</p>
                    {cols.map(({ key, label }) => {
                      const isEnabled = enabledColumns.includes(key);
                      const isDisabled = !isEnabled && nonFixedEnabled.length >= maxNonFixed;
                      return (
                        <button
                          key={key} type="button"
                          onClick={() => handleToggleColumn(key)}
                          disabled={isDisabled}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[color:var(--surface-sunken)] cursor-pointer'}`}
                        >
                          <span className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${isEnabled ? 'bg-[color:var(--trodo-primary)] border-[color:var(--trodo-primary)]' : 'border-[color:var(--border-strong)]'}`}>
                            {isEnabled && <Check size={9} strokeWidth={3} className="text-ink-onBrand" />}
                          </span>
                          <span className="flex-1 text-[12px] text-[color:var(--text-primary)] truncate">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center px-4 py-2.5 border-t border-[color:var(--border-default)] shrink-0 bg-surface">
            {hasChanges && <span className="text-[11px] text-[color:var(--text-muted)] mr-auto">Unsaved changes</span>}
            <button
              onClick={handleSave} disabled={!hasChanges || saving || loading}
              className="ml-auto px-3 py-1.5 text-[12px] font-medium text-ink-onBrand rounded-md transition-colors disabled:opacity-50"
              style={{ backgroundColor: hasChanges && !saving && !loading ? primary : 'var(--text-disabled)' }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

const DraggableItem = ({ columnKey, index, label, isRequired, onMove }) => {
  const [{ isDragging }, drag] = useDrag({ type: 'group-column', item: () => ({ columnKey, index }), collect: m => ({ isDragging: m.isDragging() }) });
  const [{ isOver }, drop] = useDrop({
    accept: 'group-column',
    hover: (item) => { if (item.index !== index) { onMove(item.index, index); item.index = index; } },
    collect: m => ({ isOver: m.isOver() })
  });
  return (
    <div ref={n => drag(drop(n))} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-move transition-colors ${isDragging ? 'opacity-40' : isOver ? 'bg-[color:var(--border-subtle)]' : 'hover:bg-[color:var(--surface-sunken)]'}`}>
      <GripVertical size={13} className="text-[color:var(--border-strong)] shrink-0" />
      <span className="flex-1 text-[12px] text-[color:var(--text-primary)] truncate">{label}</span>
      {isRequired && <span className="text-[10px] text-[color:var(--text-muted)]">required</span>}
    </div>
  );
};

export default GroupColumnConfigModal;
